import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, ShoppingCart, Share2, MessageCircle, Check } from 'lucide-react';
import { api, getPhotoUrl } from '../api';
import { useApp } from '../contexts/AppContext';
import SheepIcon from '../components/SheepIcon';

export default function SheepDetailPage() {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const { t, lang, formatPrice, addToCart, isInCart, currentCountry, whatsappNumber } = useApp();
  const [sheep,    setSheep]    = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [notFound, setNotFound] = useState(false);

  const inCart    = sheep ? isInCart(sheep.id) : false;
  const BackIcon  = lang === 'ar' ? ArrowLeft : ArrowRight;
  const isRtl     = lang === 'ar';

  useEffect(() => {
    setLoading(true);
    api.getSheep(id)
      .then(r => { setSheep(r.data); setLoading(false); })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [id]);

  /* ── Loading ── */
  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
      <div className="spinner" style={{ width: 40, height: 40 }} />
    </div>
  );

  /* ── Not found ── */
  if (notFound) return (
    <div className="empty-state" style={{ minHeight: 400 }}>
      <SheepIcon size={64} color="#7A1010" opacity={0.18} />
      <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-2)' }}>{t.notAvailable}</div>
      <button className="btn btn-primary" onClick={() => navigate('/')} style={{ marginTop: 4 }}>
        {t.home}
      </button>
    </div>
  );

  const breedLabel = lang === 'ar' ? sheep.breed_ar : sheep.breed_en;
  const photoUrl   = getPhotoUrl(sheep.photo_url);

  const handleShare = () => {
    const shareUrl = `${window.location.origin}/api/public/share/sheep/${sheep.id}`;
    const label    = breedLabel || sheep.given_name;
    const text     = `${label} — ${formatPrice(sheep.online_price)}\n${shareUrl}`;
    if (navigator.share) {
      navigator.share({ title: label, text, url: shareUrl });
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    }
  };

  const handleWhatsApp = () => {
    const num = whatsappNumber?.replace(/\D/g, '');
    if (!num) return;
    const text = lang === 'ar'
      ? `مرحباً، أريد الاستفسار عن: ${breedLabel || sheep.given_name} - السعر: ${formatPrice(sheep.online_price)}`
      : `Hello, I'm interested in: ${breedLabel || sheep.given_name} - Price: ${formatPrice(sheep.online_price)}`;
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(text)}`, '_blank');
  };

  const canBuy = currentCountry?.buying_enabled;

  const infoItems = [
    {
      label: lang === 'ar' ? 'الجنس'   : 'Gender',
      value: sheep.gender === 'male'
        ? (lang === 'ar' ? '♂ ذكر' : '♂ Male')
        : (lang === 'ar' ? '♀ أنثى' : '♀ Female'),
    },
    {
      label: lang === 'ar' ? 'النوع' : 'Breed',
      value: (lang === 'ar' ? sheep.breed_ar : sheep.breed_en) || '—',
    },
  ];

  return (
    <div className="page-pb page-center fade-up">

      {/* ── Back button ── */}
      <div style={{ padding: '14px 16px 8px', display: 'flex', alignItems: 'center' }}>
        <button
          onClick={() => navigate(-1)}
          aria-label={lang === 'ar' ? 'رجوع' : 'Back'}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--brand)',
            fontWeight: 600,
            fontSize: 14,
            fontFamily: 'inherit',
            padding: '6px 0',
          }}
        >
          <BackIcon size={17} />
          {lang === 'ar' ? 'رجوع' : 'Back'}
        </button>
      </div>

      {/* ── Photo ── */}
      <div
        style={{
          position: 'relative',
          background: 'linear-gradient(135deg, #F5F0EC 0%, #EDE8E3 100%)',
          minHeight: 260,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={breedLabel || sheep.given_name}
            style={{
              width: '100%',
              maxHeight: 380,
              objectFit: 'contain',
              display: 'block',
            }}
          />
        ) : (
          <div style={{ padding: '40px 0' }}>
            <SheepIcon size={100} color="#7A1010" opacity={0.20} />
          </div>
        )}

        {/* New badge */}
        {sheep.is_new && (
          <span
            className="badge badge-brand"
            style={{
              position: 'absolute',
              top: 14,
              ...(isRtl ? { left: 14 } : { right: 14 }),
              fontSize: 11,
              padding: '4px 12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            }}
          >
            {t.newBadge}
          </span>
        )}

        {/* Share button overlay */}
        <button
          onClick={handleShare}
          aria-label={t.share}
          style={{
            position: 'absolute',
            bottom: 14,
            ...(isRtl ? { right: 14 } : { left: 14 }),
            background: 'rgba(255,255,255,0.92)',
            border: 'none',
            borderRadius: '50%',
            width: 40,
            height: 40,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: 'var(--shadow-md)',
          }}
        >
          <Share2 size={16} color="#57534E" />
        </button>
      </div>

      {/* ── Details card ── */}
      <div
        className="card"
        style={{
          margin: '16px 16px 0',
          padding: '20px 20px 16px',
        }}
      >
        {/* Name */}
        <h1
          style={{
            fontSize: 22,
            fontWeight: 800,
            color: 'var(--text-1)',
            lineHeight: 1.25,
            marginBottom: 10,
          }}
        >
          {breedLabel || sheep.given_name}
        </h1>

        {/* Price */}
        <div
          style={{
            fontSize: 28,
            fontWeight: 800,
            color: 'var(--success)',
            marginBottom: 20,
            letterSpacing: '-0.5px',
          }}
        >
          {formatPrice(sheep.online_price)}
        </div>

        {/* Info grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 10,
            marginBottom: 16,
          }}
        >
          {infoItems.map(item => (
            <div
              key={item.label}
              style={{
                background: 'var(--surface-2)',
                borderRadius: 'var(--r-md)',
                padding: '10px 14px',
                border: '1px solid var(--border)',
              }}
            >
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 4, fontWeight: 500 }}>
                {item.label}
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-1)' }}>
                {item.value}
              </div>
            </div>
          ))}
        </div>

        {/* Description */}
        {sheep.online_description && (
          <>
            <div className="divider" style={{ marginBottom: 14 }} />
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 6, fontWeight: 600 }}>
              {lang === 'ar' ? 'الوصف' : 'Description'}
            </div>
            <p style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.75 }}>
              {sheep.online_description}
            </p>
          </>
        )}
      </div>

      {/* ── Action buttons ── */}
      <div
        style={{
          padding: '14px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {canBuy ? (
          <>
            {/* Primary CTA */}
            <button
              className={`btn ${inCart ? 'btn-success' : 'btn-primary'}`}
              onClick={() => { if (!inCart) addToCart(sheep); navigate('/cart'); }}
              style={{ width: '100%', padding: '14px 0', fontSize: 16, borderRadius: 'var(--r-lg)' }}
            >
              {inCart ? <Check size={18} strokeWidth={2.5} /> : <ShoppingCart size={18} />}
              {inCart ? (lang === 'ar' ? 'عرض السلة' : 'View Cart') : t.buyNow}
            </button>

            {/* Secondary: Add to cart */}
            <button
              className="btn btn-outline"
              onClick={() => addToCart(sheep)}
              disabled={inCart}
              style={{ width: '100%', padding: '12px 0', fontSize: 15, borderRadius: 'var(--r-lg)' }}
            >
              <ShoppingCart size={16} />
              {inCart ? t.alreadyInCart : t.addToCart}
            </button>
          </>
        ) : (
          <div
            style={{
              background: 'var(--warn-bg)',
              border: '1px solid #FCD34D',
              borderRadius: 'var(--r-lg)',
              padding: '14px 16px',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 14, color: 'var(--warn-txt)', fontWeight: 500 }}>
              {t.buyingNotAvailable}
            </div>
          </div>
        )}

        {/* WhatsApp + Share row */}
        <div style={{ display: 'flex', gap: 10 }}>
          {whatsappNumber && (
            <button
              className="btn btn-whatsapp"
              onClick={handleWhatsApp}
              style={{ flex: 1, borderRadius: 'var(--r-lg)', padding: '12px 0' }}
            >
              <MessageCircle size={16} />
              {t.whatsapp}
            </button>
          )}
          <button
            className="btn btn-ghost"
            onClick={handleShare}
            style={{ flex: 1, borderRadius: 'var(--r-lg)', padding: '12px 0' }}
          >
            <Share2 size={16} />
            {t.share}
          </button>
        </div>
      </div>
    </div>
  );
}
